import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  NotificationItem,
  NotificationListResponse,
} from '@/api/istaff/notifications';
import type { NotificationsState } from './types';

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  loading: false,
  page: 1,
  lastPage: 1,
};

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setNotificationsPage: (
      state,
      { payload }: PayloadAction<NotificationListResponse>,
    ) => {
      const isFirstPage = payload.meta.current_page === 1;
      state.items = isFirstPage
        ? payload.data
        : [...state.items, ...payload.data];
      state.unreadCount = payload.meta.unread_count;
      state.page = payload.meta.current_page;
      state.lastPage = payload.meta.last_page;
      state.loading = false;
    },
    setUnreadCount: (state, { payload }: PayloadAction<number>) => {
      state.unreadCount = payload;
    },
    markItemRead: (state, { payload }: PayloadAction<string>) => {
      const item = state.items.find((n) => n.id === payload);
      if (item && !item.read_at) {
        item.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markItemUnread: (state, { payload }: PayloadAction<string>) => {
      const item = state.items.find((n) => n.id === payload);
      if (item && item.read_at) {
        item.read_at = null;
        state.unreadCount += 1;
      }
    },
    setAllRead: (state) => {
      state.items.forEach((n) => {
        n.read_at = n.read_at ?? new Date().toISOString();
      });
      state.unreadCount = 0;
    },
    prependNotification: (
      state,
      { payload }: PayloadAction<NotificationItem>,
    ) => {
      state.items.unshift(payload);
      if (!payload.read_at) {
        state.unreadCount += 1;
      }
    },
  },
});

export const { reducer: notificationsReducer } = notificationsSlice;
